import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import {
  BehaviorSubject,
  catchError,
  filter,
  switchMap,
  take,
  throwError,
  timeout,
} from 'rxjs';
import { Router } from '@angular/router';

let isRefreshing = false;
let isRedirecting = false;
let refreshTokenSubject = new BehaviorSubject<string | null>(null);

/**
 * Extrae el redirectUrl limpio: usa el path original que disparó el 401,
 * nunca la URL del login (evita redirects anidados).
 */
function getCleanRedirectUrl(router: Router): string | null {
  const currentUrl = router.url;
  // Si ya estamos en /auth/login, no capturar como redirect
  if (currentUrl.startsWith('/auth/login') || currentUrl === '/') {
    return null;
  }
  // Extraer solo el path sin query params de redirect previos
  const [path] = currentUrl.split('?');
  return path;
}

function redirectToLogin(router: Router, authService: AuthService): void {
  if (isRedirecting) return;
  isRedirecting = true;

  authService.logout();
  const redirectUrl = getCleanRedirectUrl(router);
  const queryParams = redirectUrl ? { redirectUrl } : {};

  router.navigate(['/auth/login'], { queryParams }).then(() => {
    isRedirecting = false;
  });
}

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Excluir el endpoint de refresh del interceptor para evitar que se intercepte a sí mismo
  if (req.url.includes('/api/v1/auth/refresh') || req.url.endsWith('/auth/refresh')) {
    return next(req);
  }

  const token: any = authService.getToken();
  const authReq = buildAuthRequest(req, token);

  return next(authReq).pipe(
    catchError((err) => {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refreshToken().pipe(
            switchMap((newToken) => {
              isRefreshing = false;
              refreshTokenSubject.next(newToken);
              const retryReq = buildAuthRequest(req, newToken);
              return next(retryReq);
            }),
            catchError((refreshError) => {
              isRefreshing = false;
              refreshTokenSubject = new BehaviorSubject<string | null>(null);
              redirectToLogin(router, authService);
              return throwError(() => refreshError);
            })
          );
        } else {
          // Si ya se está refrescando, esperar a que termine
          return refreshTokenSubject.pipe(
            filter((token) => token !== null),
            take(1),
            timeout({
              each: 10000,
              with: () => {
                isRefreshing = false;
                refreshTokenSubject = new BehaviorSubject<string | null>(null);
                redirectToLogin(router, authService);
                return throwError(() => new HttpErrorResponse({
                  status: 401,
                  statusText: 'Token refresh timeout'
                }));
              }
            }),
            switchMap((token) => {
              const retryReq = buildAuthRequest(req, token!);
              return next(retryReq);
            }),
            catchError((error) => {
              if (error instanceof HttpErrorResponse && error.status === 401) {
                redirectToLogin(router, authService);
              }
              return throwError(() => error);
            })
          );
        }
      }

      return throwError(() => err);
    })
  );
};

function buildAuthRequest(
  req: HttpRequest<any>,
  token?: string
): HttpRequest<any> {
  return req.clone({
    setHeaders: {
      'X-Client-Type': 'web',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}
